import { fetchJsonWithAuth } from './authApi';
import { AUTH_URL, SUBMISSIONS_URL } from './apiConfig';

const FIELD_CONFIG_PATH = '/admin/field-config';

const CUSTOMIZABLE_MODULES = [
  { key: 'job',         apiModule: 'jobs'        },
  { key: 'candidate',   apiModule: 'candidates'  },
  { key: 'submissions', apiModule: 'submissions' },
];

// ── Shared Utilities ──────────────────────────────────────────────────────────

function firstArray(...values) {
  return values.find(Array.isArray) ?? [];
}

function firstValue(record, keys, fallback) {
  return keys.map((k) => record?.[k]).find((v) => v !== undefined && v !== null && v !== '') ?? fallback;
}

function normalizeTotal(payload, items) {
  return (
    payload?.total ?? payload?.totalCount ?? payload?.count ??
    payload?.recordsTotal ?? payload?.meta?.total ?? payload?.pagination?.total ?? items.length
  );
}

function normalizeModuleField(field, index) {
  const fieldName = firstValue(field, ['label', 'Label', 'fieldName', 'field_name', 'name', 'fieldLabel', 'value'], `Field ${index + 1}`);
  const fieldKey  = firstValue(field, ['field', 'Field', 'fieldKey', 'field_key', 'key', 'value', 'fieldName', 'name'], fieldName);
  const showValue = firstValue(field, ['isVisible', 'is_visible', 'visible', 'show', 'is_show', 'isShow', 'enabled'], true);
  const mandatoryValue = firstValue(field, ['ismandatory', 'isMandatory', 'is_mandatory', 'mandatory', 'required', 'req'], false);
  const orderValue = firstValue(field, ['order', 'Order', 'sortOrder', 'position'], index);

  return {
    ...field,
    fieldKey,
    fieldName,
    type: firstValue(field, ['type', 'Type'], 'text'),
    isVisible: typeof showValue === 'string'
      ? !['false', '0', 'hide', 'hidden', 'no'].includes(showValue.toLowerCase())
      : Boolean(showValue),
    ismandatory: typeof mandatoryValue === 'string'
      ? ['true', '1', 'yes', 'required'].includes(mandatoryValue.toLowerCase())
      : Boolean(mandatoryValue),
    order: typeof orderValue === 'number' ? orderValue : index,
    isLink:             Boolean(field.isLink),
    linkTemplate:       field.linkTemplate       ?? '',
    linkType:           field.linkType           ?? 'internal',
    linkTarget:         field.linkTarget         ?? '_self',
    action:             field.action             ?? 'navigate',
    secondaryField:     field.secondaryField     ?? '',
    secondaryLabel:     field.secondaryLabel     ?? '',
    secondaryFields:    Array.isArray(field.secondaryFields) ? field.secondaryFields : [],
    secondarySeparator: field.secondarySeparator ?? '',
    lookup: field.lookup
      ? { ...field.lookup, projectFields: Array.isArray(field.lookup.projectFields) ? field.lookup.projectFields : [] }
      : null,
    actionButtons:   Array.isArray(field.actionButtons)   ? field.actionButtons   : [],
    renderType:      field.renderType      ?? '',
    renderConfig:    field.renderConfig    ?? null,
    valueStyles:     Array.isArray(field.valueStyles)     ? field.valueStyles     : [],
    commonStyle:     field.commonStyle     ?? null,
    displayTemplate: field.displayTemplate ?? '',
    dataSource:      field.dataSource      ?? 'module',
    groupName:       field.groupName       ?? '',
  };
}

// ── Modules ───────────────────────────────────────────────────────────────────

export async function getModules() {
  const json = await fetchJsonWithAuth(AUTH_URL, '/admin/modules');
  const payload = json?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(json)) return json;
  return [];
}

// ── Admin Form Groups ─────────────────────────────────────────────────────────

const FORM_GROUPS_PATH = '/admin/form-groups';
const CONFIG_DEFAULTS_PATH = '/admin/config-defaults';

function formGroupScopeQuery(scope = {}) {
  const params = new URLSearchParams();
  if (scope.module) params.set('module', scope.module);
  if (scope.clientId) params.set('clientId', scope.clientId);
  if (scope.region) params.set('region', scope.region);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getAdminFormGroups(module = '', scope = {}) {
  const query = formGroupScopeQuery({ ...scope, module: module || scope.module || '' });
  const path = `${FORM_GROUPS_PATH}${query}`;
  const json = await fetchJsonWithAuth(AUTH_URL, path);
  const payload = json?.data;
  // Backend returns EITHER a flat array of groups, OR a wrapper object
  // { id, module, ..., groups: [...] }. Support both shapes.
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.groups)) return payload.groups;
  if (Array.isArray(json)) return json;
  if (json && Array.isArray(json.groups)) return json.groups;
  return [];
}

export async function getAdminClients({ offset = 0, limit = 100, sortBy = 'new', searchvalue = '' } = {}) {
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
    sortBy,
    searchvalue,
  });
  // Scope the list to the active tenant/business/businessUnit captured from the
  // login token (authApi stores these on login). The backend uses them when
  // present, otherwise falls back to the token claims — so the dropdown lists
  // the right clients for the selected project, same as the legacy flow.
  const tenantId = localStorage.getItem('tenantId') || '';
  const businessId = localStorage.getItem('businessId') || '';
  const businessUnitId = localStorage.getItem('businessUnitId') || '';
  if (tenantId) params.set('tenantId', tenantId);
  if (businessId) params.set('businessId', businessId);
  if (businessUnitId) params.set('businessUnitId', businessUnitId);
  const json = await fetchJsonWithAuth(AUTH_URL, `/client/view?${params.toString()}`);
  const data = json?.data ?? json;
  return data?.clients ?? data?.Clients ?? data?.items ?? [];
}

export async function getModuleFields(module) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/filter-dropdown-fields?module=${encodeURIComponent(module)}`);
  const fields = json?.data ?? json;
  return Array.isArray(fields) ? fields : [];
}

export async function createAdminFormGroup(group, scope = {}) {
  return fetchJsonWithAuth(AUTH_URL, `${FORM_GROUPS_PATH}${formGroupScopeQuery(scope)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(group),
  });
}

export async function updateAdminFormGroup(id, group, scope = {}) {
  return fetchJsonWithAuth(AUTH_URL, `${FORM_GROUPS_PATH}/${id}${formGroupScopeQuery(scope)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(group),
  });
}

export async function deleteAdminFormGroup(id) {
  return fetchJsonWithAuth(AUTH_URL, `${FORM_GROUPS_PATH}/${id}`, { method: 'DELETE' });
}

export async function seedAdminFormGroups() {
  return fetchJsonWithAuth(AUTH_URL, `${FORM_GROUPS_PATH}/seed`, { method: 'POST' });
}

export async function requestConfigOtp(scope, action) {
  return fetchJsonWithAuth(AUTH_URL, `${CONFIG_DEFAULTS_PATH}/request-otp`, {
    method: 'POST',
    body: JSON.stringify({ scope, action }),
  });
}

export async function applyConfigDefault(scope, action, otp) {
  return fetchJsonWithAuth(AUTH_URL, `${CONFIG_DEFAULTS_PATH}/apply`, {
    method: 'POST',
    body: JSON.stringify({ scope, action, otp }),
  });
}

// ── Admin Detail-View Config ──────────────────────────────────────────────────
// Per-module customization layered on top of the form groups, applied only by
// the detail view (?view=detail). Shape: { module, fields:[…], merges:[…] }.

const DETAIL_CONFIG_PATH = '/admin/detail-config';

export async function getDetailConfig(module) {
  const json = await fetchJsonWithAuth(AUTH_URL, `${DETAIL_CONFIG_PATH}?module=${encodeURIComponent(module)}`);
  const data = json?.data ?? json ?? {};
  return {
    module: data.module ?? module,
    fields: Array.isArray(data.fields) ? data.fields : [],
    merges: Array.isArray(data.merges) ? data.merges : [],
  };
}

export async function saveDetailConfig({ module, fields = [], merges = [] }) {
  return fetchJsonWithAuth(AUTH_URL, DETAIL_CONFIG_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ module, fields, merges }),
  });
}

// ── Detail-View Display Defaults ──────────────────────────────────────────────
// Tenant-wide display settings (S3 base, date format, boolean/empty labels,
// separator, document name order) — the configurable home of what used to be
// hard-coded in fileUtils.js / FieldValue.jsx.

const DETAIL_DEFAULTS_PATH = '/admin/detail-defaults';

export async function getDetailDefaults() {
  const json = await fetchJsonWithAuth(AUTH_URL, DETAIL_DEFAULTS_PATH);
  return json?.data ?? json ?? {};
}

export async function saveDetailDefaults(defaults) {
  return fetchJsonWithAuth(AUTH_URL, DETAIL_DEFAULTS_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(defaults),
  });
}

// getModuleDetailPreview fetches a record's detail-view groups WITH resolved
// values (references → {id,value}, documents → full object) so the admin page
// can preview exactly what the detail view will render.
export async function getModuleDetailPreview(module, id) {
  const path = `${FORM_GROUPS_PATH}?module=${encodeURIComponent(module)}&id=${encodeURIComponent(id)}&view=detail`;
  const json = await fetchJsonWithAuth(AUTH_URL, path);
  const data = json?.data ?? json;
  return Array.isArray(data) ? data : [];
}

// ── Roles ─────────────────────────────────────────────────────────────────────

function normalizeRole(record) {
  return {
    key:         record.role_id ?? record.id,
    roleId:      record.role_id ?? record.id,
    roleName:    record.role_name        ?? 'N/A',
    description: record.role_description ?? 'N/A',
    userCount:   record.user_count       ?? 0,
    roleType:    record.role_type        ?? 0,
    raw: record,
  };
}

export function getRoleId(role) {
  return role?.raw?.role_id ?? role?.roleId ?? role?.key;
}

export async function getAllRoles() {
  const json = await fetchJsonWithAuth(AUTH_URL, '/get-roles');
  const payload = json.data ?? json;
  const defaultRoles = firstArray(payload?.zinnext_default).map(normalizeRole);
  const customRoles  = firstArray(payload?.custom_roles).map(normalizeRole);
  const all = [...defaultRoles, ...customRoles];
  return { roles: all, total: all.length };
}

// ── Teams ─────────────────────────────────────────────────────────────────────

function normalizeTeam(record) {
  return {
    key:         record.teamId,
    teamId:      record.teamId,
    teamName:    record.teamName           ?? 'N/A',
    manager:     record.reportingManager   ?? 'N/A',
    managerId:   record.reportingManagerId ?? 0,
    memberCount: record.noOfTeamMembers    ?? 0,
    members:     firstArray(record.teamMembers),
    memberIds:   firstArray(record.teamMembersIds),
    raw: record,
  };
}

export function getTeamId(team) {
  return team?.raw?.teamId ?? team?.teamId ?? team?.key;
}

export async function getAllTeams({ offset = 0, limit = 10, sortBy = 'new', search = '' } = {}) {
  const params = new URLSearchParams({ searchquery: search, sortBy, limit: String(limit), offset: String(offset) });
  const json = await fetchJsonWithAuth(AUTH_URL, `/get-teams?${params}`);
  const payload = json.data ?? json;
  const teams = firstArray(payload?.teams, payload, json?.teams);
  const total = payload?.totalCount ?? payload?.total ?? teams.length;
  return { teams: teams.map(normalizeTeam), total };
}

export async function updateTeam(team, { teamName, managerId, memberIds = [] }) {
  const teamId = getTeamId(team);
  return fetchJsonWithAuth(AUTH_URL, `/team/edit?teamId=${teamId}`, {
    method: 'PUT',
    body: JSON.stringify({
      team_name: teamName,
      team_manager: managerId,
      other_team_members: memberIds,
      reporting_team_member: [],
    }),
  });
}

// ── Role / Team Field Config ──────────────────────────────────────────────────

async function fetchFieldConfigModule(apiModule, idParam, idValue, configType = 'listView') {
  const params = new URLSearchParams({ module: apiModule, userId: '0', [idParam]: String(idValue), configType });
  const json = await fetchJsonWithAuth(AUTH_URL, `${FIELD_CONFIG_PATH}?${params}`);
  const payload = json.data ?? json;
  return firstArray(payload, payload?.visibleFields, json?.visibleFields);
}

export async function getRoleModuleFields(role, configType = 'listView') {
  const roleId = getRoleId(role);
  const entries = await Promise.all(
    CUSTOMIZABLE_MODULES.map(async ({ key, apiModule }) => {
      const fields = await fetchFieldConfigModule(apiModule, 'roleId', roleId, configType);
      return [key, fields.map((f, i) => ({ ...normalizeModuleField(f, i), module: key, apiModule, roleId }))];
    })
  );
  return Object.fromEntries(entries);
}

export async function getTeamModuleFields(team, configType = 'listView') {
  const teamId = getTeamId(team);
  const entries = await Promise.all(
    CUSTOMIZABLE_MODULES.map(async ({ key, apiModule }) => {
      const fields = await fetchFieldConfigModule(apiModule, 'teamId', teamId, configType);
      return [key, fields.map((f, i) => ({ ...normalizeModuleField(f, i), module: key, apiModule, teamId }))];
    })
  );
  return Object.fromEntries(entries);
}

function visibleFieldPayload(fields, configType) {
  const isListView = !configType || configType === 'listView';
  return fields.map((field, index) => {
    const base = {
      label: field.fieldName, field: field.fieldKey,
      isVisible: field.isVisible, type: field.type ?? 'text', order: field.order ?? index,
    };
    if (isListView) {
      Object.assign(base, {
        isLink: field.isLink ?? false, linkTemplate: field.linkTemplate ?? '',
        linkType: field.linkType ?? 'internal', linkTarget: field.linkTarget ?? '_self',
        action: field.action ?? 'navigate',
        secondaryField: field.secondaryField ?? '', secondaryLabel: field.secondaryLabel ?? '',
        secondaryFields: Array.isArray(field.secondaryFields) ? field.secondaryFields : [],
        secondarySeparator: field.secondarySeparator ?? '',
        lookup: field.lookup ?? null, actionButtons: field.actionButtons ?? [],
        renderType: field.renderType ?? '', renderConfig: field.renderConfig ?? null,
        valueStyles: Array.isArray(field.valueStyles) ? field.valueStyles : [],
        commonStyle: field.commonStyle ?? null, displayTemplate: field.displayTemplate ?? '',
      });
    } else {
      base.isEditable  = field.isEditable  ?? false;
      base.ismandatory = field.ismandatory ?? field.isMandatory ?? false;
      if (configType === 'filter') {
        base.filterInputType  = field.filterInputType ?? 'dropdown';
        base.isDefaultFilter  = Boolean(field.isDefaultFilter);
        base.dataSource       = field.dataSource ?? 'module';
        if (field.dataSource === 'group') base.groupName = field.groupName ?? '';
        if (field.lookup) base.lookup = field.lookup;
      } else {
        if (['select', 'radio', 'checkbox'].includes(field.type)) {
          base.dataSource = field.dataSource ?? 'module';
          if (field.dataSource === 'group') base.groupName = field.groupName ?? '';
        }
      }
    }
    return base;
  });
}

export async function updateRoleModuleFieldConfig(role, module, fields, configType = 'listView') {
  const roleId    = getRoleId(role);
  const apiModule = CUSTOMIZABLE_MODULES.find((m) => m.key === module)?.apiModule ?? module;
  return fetchJsonWithAuth(AUTH_URL, `${FIELD_CONFIG_PATH}/role`, {
    method: 'PUT',
    body: JSON.stringify({ module: apiModule, roleId, configType, visibleFields: visibleFieldPayload(fields, configType) }),
  });
}

export async function updateTeamModuleFieldConfig(team, module, fields, configType = 'listView') {
  const teamId    = getTeamId(team);
  const apiModule = CUSTOMIZABLE_MODULES.find((m) => m.key === module)?.apiModule ?? module;
  return fetchJsonWithAuth(AUTH_URL, `${FIELD_CONFIG_PATH}/team`, {
    method: 'PUT',
    body: JSON.stringify({ module: apiModule, teamId, configType, visibleFields: visibleFieldPayload(fields, configType) }),
  });
}

// ── Users ─────────────────────────────────────────────────────────────────────

export async function getAllUsers({ offset = 0, limit = 10, sortBy = 'new', searchQuery = '' } = {}) {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit), sortBy });
  if (searchQuery) params.set('searchquery', searchQuery);
  const json = await fetchJsonWithAuth(AUTH_URL, `/get-all-users?${params}`);
  const payload = json.data ?? json;
  const users = firstArray(
    payload, json.users, json.rows, json.items, json.records,
    payload?.users, payload?.data, payload?.rows, payload?.items,
    payload?.records, payload?.docs, payload?.result, payload?.results,
  );
  return { users, total: normalizeTotal({ ...json, ...payload }, users) };
}

export function getUserId(user) {
  const value = firstValue(
    user?.raw ?? user,
    ['USER_ID', 'user_id', 'userId', 'id', '_id', 'uuid'],
    user?.key
  );
  const num = Number(value);
  return Number.isFinite(num) ? num : value;
}

export function getUserRoleId(user) {
  const value = firstValue(user?.raw ?? user, ['ROLE_ID', 'role_id', 'roleId', 'ROLEID'], 0);
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

async function getFieldConfigModule(apiModule, userId, roleId, configType) {
  const params = new URLSearchParams({ module: apiModule, userId: String(userId ?? ''), roleId: String(roleId ?? ''), configType });
  const json = await fetchJsonWithAuth(AUTH_URL, `${FIELD_CONFIG_PATH}?${params}`);
  const payload = json.data ?? json;
  return firstArray(payload, payload?.visibleFields, json?.visibleFields);
}

async function getDropdownModuleFields(apiModule) {
  const json = await fetchJsonWithAuth(SUBMISSIONS_URL, `/filter-dropdown-fields?module=${encodeURIComponent(apiModule)}`);
  const payload = json.data ?? json;
  return firstArray(payload, payload?.fields, payload?.items, payload?.rows, json?.fields);
}

export async function getUserModuleFields(user, configType = 'listView') {
  const userId = getUserId(user);
  const roleId = getUserRoleId(user);
  const entries = await Promise.all(
    CUSTOMIZABLE_MODULES.map(async ({ key, apiModule }) => {
      let fields;
      try {
        fields = await getFieldConfigModule(apiModule, userId, roleId, configType);
      } catch {
        try { fields = await getDropdownModuleFields(apiModule); } catch { fields = []; }
      }
      if ((fields?.length ?? 0) === 0) {
        try { fields = await getDropdownModuleFields(apiModule); } catch { fields = []; }
      }
      return [key, fields.map((f, i) => ({ ...normalizeModuleField(f, i), module: key, apiModule, userId, roleId }))];
    })
  );
  return Object.fromEntries(entries);
}

export async function updateUserModuleFieldConfig(user, module, fields, configType = 'listView') {
  const userId    = getUserId(user);
  const roleId    = getUserRoleId(user);
  const apiModule = CUSTOMIZABLE_MODULES.find((m) => m.key === module)?.apiModule ?? module;
  return fetchJsonWithAuth(AUTH_URL, FIELD_CONFIG_PATH, {
    method: 'PUT',
    body: JSON.stringify({ module: apiModule, userId, roleId, configType, visibleFields: visibleFieldPayload(fields, configType) }),
  });
}

export async function updateUserModuleFields(user, moduleFields, configType = 'listView') {
  return Promise.all(
    Object.entries(moduleFields).map(([module, fields]) => updateUserModuleFieldConfig(user, module, fields, configType))
  );
}

export async function uploadFormGroupIcon(file) {
  const token = localStorage.getItem('authToken');
  const formData = new FormData();
  formData.append('icon', file);
  const res = await fetch(`${AUTH_URL}/admin/form-groups/icon`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) { const e = new Error(`Upload failed: ${res.status}`); e.status = res.status; throw e; }
  const json = await res.json();
  return json.data ?? json;
}

export async function uploadListViewActionIcon(file) {
  const token = localStorage.getItem('authToken');
  const formData = new FormData();
  formData.append('icon', file);
  const res = await fetch(`${AUTH_URL}/admin/field-config/action-icon`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) { const e = new Error(`Upload failed: ${res.status}`); e.status = res.status; throw e; }
  const json = await res.json();
  return json.data ?? json;
}

export async function getAvailableCollections() {
  const json = await fetchJsonWithAuth(AUTH_URL, '/admin/lookup/collections');
  const data = json.data ?? json;
  return Array.isArray(data) ? data : [];
}

export async function getCollectionFields(collection) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/admin/lookup/collection-fields?collection=${encodeURIComponent(collection)}`);
  const data = json.data ?? json;
  return Array.isArray(data) ? data : [];
}

export async function getArraySubfields(module, field) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/admin/field-array-subfields?module=${encodeURIComponent(module)}&field=${encodeURIComponent(field)}`);
  const data = json.data ?? json;
  return Array.isArray(data?.fields) ? data.fields : [];
}

// ── Menu Modules & Actions (master data management) ──────────────────────────

export async function getMenuModules() {
  const json = await fetchJsonWithAuth(AUTH_URL, '/admin/menu-modules');
  return firstArray(json.data, json);
}

export async function createMenuModule({ menuName, apiUrl = '', menuType = 'menu', parentMenuId = 0, displayOrder = 0 }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/admin/menu-modules', {
    method: 'POST',
    body: JSON.stringify({ menuName, apiUrl, menuType, parentMenuId, displayOrder }),
  });
  return json.data ?? json;
}

export async function updateMenuModule(id, { menuName }) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/admin/menu-modules/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ menuName }),
  });
  return json.data ?? json;
}

export async function deleteMenuModule(id) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/admin/menu-modules/${id}`, { method: 'DELETE' });
  return json.data ?? json;
}

export async function getMenuActions() {
  const json = await fetchJsonWithAuth(AUTH_URL, '/admin/menu-actions');
  return firstArray(json.data, json);
}

export async function createMenuAction({ permissionName, permissionKey }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/admin/menu-actions', {
    method: 'POST',
    body: JSON.stringify({ permissionName, permissionKey }),
  });
  return json.data ?? json;
}

export async function updateMenuAction(id, { permissionName }) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/admin/menu-actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ permissionName }),
  });
  return json.data ?? json;
}

export async function deleteMenuAction(id) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/admin/menu-actions/${id}`, { method: 'DELETE' });
  return json.data ?? json;
}

// ── Permissions ───────────────────────────────────────────────────────────────

export async function getRolePermissions(roleId) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/get-role-details?roleid=${roleId}`);
  return (json?.data ?? json) ?? {};
}

export async function updateRolePermissions(roleId, menus) {
  return fetchJsonWithAuth(AUTH_URL, '/edit-role-details', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roleId, menus }),
  });
}

export async function getUserPermissions(userId) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/get-role-details?userId=${userId}`);
  return (json?.data ?? json) ?? {};
}

export async function updateUserPermissions(userId, menus) {
  return fetchJsonWithAuth(AUTH_URL, '/edit-role-details', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, menus }),
  });
}

// ── Custom Forms ──────────────────────────────────────────────────────────────

const CUSTOM_FORMS_PATH = '/admin/custom-forms';

export async function getCustomForms(module = '', type = '', action = '') {
  const params = new URLSearchParams();
  if (module) params.append('module', module);
  if (type) params.append('formType', type);
  if (action) params.append('action', action);
  const path = `${CUSTOM_FORMS_PATH}?${params.toString()}`;
  const json = await fetchJsonWithAuth(AUTH_URL, path);
  const payload = json?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(json)) return json;
  return [];
}

export async function createCustomForm(form) {
  return fetchJsonWithAuth(AUTH_URL, CUSTOM_FORMS_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(form),
  });
}

export async function updateCustomForm(id, form) {
  return fetchJsonWithAuth(AUTH_URL, `${CUSTOM_FORMS_PATH}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(form),
  });
}

export async function deleteCustomForm(id) {
  return fetchJsonWithAuth(AUTH_URL, `${CUSTOM_FORMS_PATH}/${id}`, { method: 'DELETE' });
}

// ── Row Action Config ─────────────────────────────────────────────────────────

const ROW_ACTION_CONFIG_PATH = '/admin/row-action-config';

export async function getRowActionConfigs(module = '') {
  const path = module
    ? `${ROW_ACTION_CONFIG_PATH}?module=${encodeURIComponent(module)}`
    : ROW_ACTION_CONFIG_PATH;
  const json = await fetchJsonWithAuth(AUTH_URL, path);
  const payload = json?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(json)) return json;
  return [];
}

export async function createRowActionConfig({ module, roleId = 0, rowActions = [] }) {
  return fetchJsonWithAuth(AUTH_URL, ROW_ACTION_CONFIG_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ module, roleId, rowActions }),
  });
}

export async function updateRowActionConfig(id, rowActions = []) {
  return fetchJsonWithAuth(AUTH_URL, `${ROW_ACTION_CONFIG_PATH}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rowActions }),
  });
}

export async function deleteRowActionConfig(id) {
  return fetchJsonWithAuth(AUTH_URL, `${ROW_ACTION_CONFIG_PATH}/${id}`, { method: 'DELETE' });
}

export async function seedRowActionConfigs() {
  return fetchJsonWithAuth(AUTH_URL, `${ROW_ACTION_CONFIG_PATH}/seed`, { method: 'POST' });
}

// ── Module Action Rules ───────────────────────────────────────────────────────
// Per-module configurable rules stored in auth repo, returned by module-data-list.
// Frontend evaluates these rules per-row to show/hide row action buttons.

const MODULE_ACTION_RULES_PATH = '/admin/module-action-rules';

export async function getModuleActionRules(module) {
  const json = await fetchJsonWithAuth(AUTH_URL, `${MODULE_ACTION_RULES_PATH}?module=${encodeURIComponent(module)}`);
  const payload = json?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(json)) return json;
  return [];
}

export async function createModuleActionRule({ module, name, description = '', conditions = [], blockedActions = [], isActive = true }) {
  return fetchJsonWithAuth(AUTH_URL, MODULE_ACTION_RULES_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ module, name, description, conditions, blockedActions, isActive }),
  });
}

export async function updateModuleActionRule(id, { name, description = '', conditions = [], blockedActions = [], isActive = true }) {
  return fetchJsonWithAuth(AUTH_URL, `${MODULE_ACTION_RULES_PATH}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description, conditions, blockedActions, isActive }),
  });
}

export async function deleteModuleActionRule(id) {
  return fetchJsonWithAuth(AUTH_URL, `${MODULE_ACTION_RULES_PATH}/${id}`, { method: 'DELETE' });
}
